import type { TableHeadCellProps as OriginalTableHeadCellProps } from 'src/components/table';

import {
  Box,
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  CardContent,
  TableContainer,
} from '@mui/material';

type TableHeadCellProps = OriginalTableHeadCellProps & {
  dataTestId?: string;
};

import { useTable, TableNoData, TableHeadCustom } from 'src/components/table';

import { formatDate } from '../../shared/utils/policyUtils';
import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';
import { usePolicyDataFromParams } from '../../shared/hooks/usePolicyData';

const TABLE_HEAD: TableHeadCellProps[] = [
  {
    id: 'cancelReinstateTypeDescription',
    label: 'Status',
    dataTestId: 'table-head-cancelReinstateTypeDescription',
  },
  {
    id: 'computedReinstateTypeDescription',
    label: 'Cancellation type',
    dataTestId: 'table-head-sub-type',
  },
  { id: 'computedReason', label: 'Reason', dataTestId: 'table-head-reason' },
  {
    id: 'cancelReinstateEffectiveDate',
    label: 'Effective date',
    dataTestId: 'table-head-effective-date',
  },
  { id: 'createdDate', label: 'Received date', dataTestId: 'table-head-received-date' },
  { id: 'issueDate', label: 'Issue date', dataTestId: 'table-head-issue-date' },
];

function StatusHistoryTable() {
  const { viewPolicy } = usePolicyDataFromParams(); // <-- add error

  // Determine no data state depending on if endpoint returns an empty array
  // not checking for 404 because this page is using the combo by id endpoint
  // and has not yet been refactored to use a seperate endpoint.
  // therefore, the app will already through an error for a non existing combo before reaching this page.
  const notFound =
    Array.isArray(viewPolicy?.policyCancelReinstatements) &&
    viewPolicy?.policyCancelReinstatements.length === 0;

  const table = useTable({
    defaultOrder: 'desc',
    defaultOrderBy: 'createdDate',
  });

  const tableData = viewPolicy?.policyCancelReinstatements?.map((item) => ({
    ...item,
    computedReinstateTypeDescription:
      item.cancelReinstateSequenceNumber === 1
        ? item.cancelTypeDescription
        : item.reinstateTypeDescription,
    computedReason:
      item.cancelReinstateSequenceNumber === 1
        ? item.cancelReasonDescription
        : item.reinstateReasonDescription,
  }));

  const comparator = (a: any, b: any) => {
    const fieldA = a[table.orderBy];
    const fieldB = b[table.orderBy];

    if (fieldA < fieldB) return table.order === 'asc' ? -1 : 1;
    if (fieldA > fieldB) return table.order === 'asc' ? 1 : -1;

    // Default: group by submissionId, then by createdDate desc, then by sequence asc
    const submissionA = a.submissionId || 0;
    const submissionB = b.submissionId || 0;

    if (submissionA !== submissionB) {
      return submissionA - submissionB;
    }

    const dateA = new Date(a.createdDate || 0).getTime();
    const dateB = new Date(b.createdDate || 0).getTime();

    if (dateA !== dateB) {
      return dateB - dateA; // descending
    }

    const seqA = a.cancelReinstateSequenceNumber || 0;
    const seqB = b.cancelReinstateSequenceNumber || 0;
    return seqA - seqB;
  };

  const sortedData = [...(tableData || [])].sort(comparator);

  return (
    <Box>
      <Typography variant="h5" sx={{ color: 'text.primary' }} data-testid="status-history-title">
        Status history
      </Typography>
      <TableContainer
        sx={{
          mt: 1.5,
          border: '1px solid var(--palette-divider)',
          borderRadius: 2,
          borderBottom: '1px dashed var(--palette-divider)',
        }}
      >
        <Table sx={{ minWidth: 500 }} data-testid="status-history-table">
          <TableHeadCustom
            order={table.order}
            orderBy={table.orderBy}
            headCells={TABLE_HEAD}
            rowCount={viewPolicy?.policyCancelReinstatements?.length}
            numSelected={table.selected.length}
            onSort={table.onSort}
            data-testid="status-history-table-head"
          />
          <TableBody data-testid="status-history-table-body">
            {sortedData?.map((row, index) => (
              <TableRow hover tabIndex={-1} key={index} data-testid={`status-history-row-${index}`}>
                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                  data-testid={`status-history-type-${index}`}
                >
                  {row.cancelReinstateTypeDescription}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                  data-testid={`status-history-sub-type-${index}`}
                >
                  {row.computedReinstateTypeDescription}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'wrap' }}
                  data-testid={`status-history-reason-${index}`}
                >
                  {row.computedReason}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                  data-testid={`status-history-effective-date-${index}`}
                >
                  {formatDate(row.cancelReinstateEffectiveDate)}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                  data-testid={`status-history-received-date-${index}`}
                >
                  {formatDate(row.createdDate)}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                  data-testid={`status-history-issue-date-${index}`}
                >
                  {formatDate(row.cancelReinstateIssueDate)}
                </TableCell>
              </TableRow>
            ))}
            <TableNoData
              notFound={notFound}
              description="This table will be updated if the policy has any cancellations or reinstatements to report."
              data-testid="status-history-no-data"
            />
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function StatusHistory() {
  const headerWidth = useHeaderWidth();

  return (
    <Card
      sx={[
        () => ({
          display: 'flex',
          zIndex: 'unset',
          overflow: 'unset',
          mt: 5,
          borderRadius: 2,
          boxShadow: 'var(--customShadows-card)',
          width: headerWidth,
        }),
      ]}
    >
      <CardContent sx={{ flexGrow: 1, padding: '0px' }} data-testid="policy-name-addresses-card">
        <Box
          sx={{
            paddingTop: '1.5rem',
            paddingRight: '1.5rem',
            paddingLeft: '1.5rem',
            padddingBottom: '2px',
          }}
        >
          <StatusHistoryTable />
        </Box>
      </CardContent>
    </Card>
  );
}
